import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Plus, Trash2, Tag, Save, Percent, Hash, Pencil, X } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const PromoCodesTab = ({ onRefresh }) => {
  const [codes, setCodes] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [newCode, setNewCode] = useState({
    code: '', discount_type: 'percent', discount_value: 10, max_uses: 0, expires_at: ''
  });

  const fetchCodes = async () => {
    try {
      const res = await axios.get(`${API}/admin/promo-codes`);
      setCodes(res.data);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { fetchCodes(); }, []);

  const createCode = async () => {
    if (!newCode.code) return;
    try {
      await axios.post(`${API}/admin/promo-codes`, {
        ...newCode,
        expires_at: newCode.expires_at || null,
      });
      setNewCode({ code: '', discount_type: 'percent', discount_value: 10, max_uses: 0, expires_at: '' });
      setShowNew(false);
      fetchCodes();
    } catch (e) {
      alert(e.response?.data?.detail || 'Erreur');
    }
  };

  const updateCode = async (id) => {
    try {
      await axios.patch(`${API}/admin/promo-codes/${id}`, editData);
      setEditingId(null);
      setEditData({});
      fetchCodes();
    } catch (e) {
      alert(e.response?.data?.detail || 'Erreur');
    }
  };

  const deleteCode = async (id) => {
    if (!window.confirm('Supprimer ce code promo ?')) return;
    try {
      await axios.delete(`${API}/admin/promo-codes/${id}`);
      fetchCodes();
    } catch (e) { console.error(e); }
  };

  const startEdit = (code) => {
    setEditingId(code.id);
    setEditData({
      code: code.code,
      discount_type: code.discount_type,
      discount_value: code.discount_value,
      max_uses: code.max_uses,
      expires_at: code.expires_at || '',
      is_active: code.is_active,
    });
  };

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-2xl p-4 border border-[#E5DCD3] flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-[#2D241D]" data-testid="promo-codes-title">Codes Promo ({codes.length})</h3>
          <p className="text-xs text-[#6E5E52]">Creez et modifiez des codes de reduction pour vos clients</p>
        </div>
        <Dialog open={showNew} onOpenChange={setShowNew}>
          <DialogTrigger asChild>
            <Button className="bg-[#E16833] hover:bg-[#C95A2A] text-white rounded-xl" data-testid="add-promo-btn">
              <Plus className="w-4 h-4 mr-2" /> Nouveau code
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md bg-white rounded-3xl admin-theme">
            <DialogHeader><DialogTitle className="text-[#2D241D]">Nouveau code promo</DialogTitle></DialogHeader>
            <div className="space-y-4 p-4">
              <div>
                <label className="text-sm font-medium text-[#2D241D] mb-1 block">Code</label>
                <Input value={newCode.code}
                  onChange={(e) => setNewCode({ ...newCode, code: e.target.value.toUpperCase() })}
                  placeholder="ex: BIENVENUE10" className="rounded-xl border-[#E5DCD3] uppercase"
                  data-testid="promo-code-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium text-[#2D241D] mb-1 block">Type</label>
                  <select value={newCode.discount_type}
                    onChange={(e) => setNewCode({ ...newCode, discount_type: e.target.value })}
                    className="w-full h-10 rounded-xl border border-[#E5DCD3] px-3 bg-white text-sm"
                    data-testid="promo-type-select">
                    <option value="percent">Pourcentage (%)</option>
                    <option value="fixed">Montant fixe (THB)</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-[#2D241D] mb-1 block">Valeur</label>
                  <Input type="number" value={newCode.discount_value}
                    onChange={(e) => setNewCode({ ...newCode, discount_value: parseFloat(e.target.value) || 0 })}
                    className="rounded-xl border-[#E5DCD3]" data-testid="promo-value-input" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium text-[#2D241D] mb-1 block">Utilisations max (0=illimite)</label>
                  <Input type="number" value={newCode.max_uses}
                    onChange={(e) => setNewCode({ ...newCode, max_uses: parseInt(e.target.value) || 0 })}
                    className="rounded-xl border-[#E5DCD3]" data-testid="promo-max-uses" />
                </div>
                <div>
                  <label className="text-sm font-medium text-[#2D241D] mb-1 block">Expire le</label>
                  <Input type="date" value={newCode.expires_at}
                    onChange={(e) => setNewCode({ ...newCode, expires_at: e.target.value })}
                    className="rounded-xl border-[#E5DCD3]" data-testid="promo-expires" />
                </div>
              </div>
              <Button onClick={createCode} disabled={!newCode.code}
                className="w-full bg-[#0A422D] hover:bg-[#083323] text-white rounded-xl" data-testid="save-promo-btn">
                <Save className="w-4 h-4 mr-2" /> Creer le code
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="space-y-3">
        {codes.map(code => (
          <div key={code.id} className="bg-white rounded-2xl p-4 border border-[#E5DCD3]" data-testid={`promo-${code.id}`}>
            {editingId === code.id ? (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-[#6E5E52] mb-1 block">Code</label>
                    <Input value={editData.code}
                      onChange={(e) => setEditData({ ...editData, code: e.target.value.toUpperCase() })}
                      className="rounded-xl border-[#E5DCD3] uppercase text-sm" data-testid={`edit-promo-code-${code.id}`} />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-[#6E5E52] mb-1 block">Type</label>
                    <select value={editData.discount_type}
                      onChange={(e) => setEditData({ ...editData, discount_type: e.target.value })}
                      className="w-full h-10 rounded-xl border border-[#E5DCD3] px-3 bg-white text-sm"
                      data-testid={`edit-promo-type-${code.id}`}>
                      <option value="percent">Pourcentage (%)</option>
                      <option value="fixed">Montant fixe (THB)</option>
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="text-xs font-medium text-[#6E5E52] mb-1 block">Valeur</label>
                    <Input type="number" value={editData.discount_value}
                      onChange={(e) => setEditData({ ...editData, discount_value: parseFloat(e.target.value) || 0 })}
                      className="rounded-xl border-[#E5DCD3] text-sm" data-testid={`edit-promo-value-${code.id}`} />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-[#6E5E52] mb-1 block">Max utilisations</label>
                    <Input type="number" value={editData.max_uses}
                      onChange={(e) => setEditData({ ...editData, max_uses: parseInt(e.target.value) || 0 })}
                      className="rounded-xl border-[#E5DCD3] text-sm" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-[#6E5E52] mb-1 block">Expire le</label>
                    <Input type="date" value={editData.expires_at || ''}
                      onChange={(e) => setEditData({ ...editData, expires_at: e.target.value })}
                      className="rounded-xl border-[#E5DCD3] text-sm" />
                  </div>
                </div>
                <div className="flex gap-2 justify-end">
                  <Button size="sm" variant="outline" onClick={() => { setEditingId(null); setEditData({}); }}
                    className="rounded-xl border-[#E5DCD3] text-[#6E5E52]">
                    <X className="w-3 h-3 mr-1" /> Annuler
                  </Button>
                  <Button size="sm" onClick={() => updateCode(code.id)}
                    className="bg-[#0A422D] hover:bg-[#083323] text-white rounded-xl" data-testid={`save-edit-promo-${code.id}`}>
                    <Save className="w-3 h-3 mr-1" /> Sauvegarder
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-[#E16833]/10 flex items-center justify-center">
                    <Tag className="w-5 h-5 text-[#E16833]" />
                  </div>
                  <div>
                    <p className="font-bold text-[#2D241D] tracking-wider">{code.code}</p>
                    <div className="flex items-center gap-2 text-xs text-[#6E5E52]">
                      <span className="flex items-center gap-0.5">
                        {code.discount_type === 'percent' ? <Percent className="w-3 h-3" /> : <Hash className="w-3 h-3" />}
                        {code.discount_type === 'percent' ? `${code.discount_value}%` : `${code.discount_value} THB`}
                      </span>
                      <span>|</span>
                      <span>{code.uses_count}{code.max_uses > 0 ? `/${code.max_uses}` : ''} utilise(s)</span>
                      {code.expires_at && <><span>|</span><span>Expire: {code.expires_at}</span></>}
                    </div>
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button size="sm" variant="ghost" onClick={() => startEdit(code)}
                    className="text-[#6E5E52] hover:text-[#0A422D]" data-testid={`edit-promo-${code.id}`}>
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => deleteCode(code.id)}
                    className="text-[#E63946] hover:text-[#E63946]" data-testid={`delete-promo-${code.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        ))}
        {codes.length === 0 && (
          <div className="text-center py-12 text-[#6E5E52] bg-white rounded-2xl border border-[#E5DCD3]">
            <Tag className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>Aucun code promo</p>
          </div>
        )}
      </div>
    </div>
  );
};
